/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { Injectable } from '@angular/core';
import { ActiveCartFacade, Cart, CreateCartEvent } from '@spartacus/cart/base/root';
import {
  AuthService,
  BASE_SITE_CONTEXT_ID,
  EventService,
  SiteContextParamsService,
  StatePersistenceService,
  StorageSyncType,
} from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeWhile,
} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GonvarriMiniCartComponentService {
  constructor(
    protected activeCartFacade: ActiveCartFacade,
    protected authService: AuthService,
    protected statePersistenceService: StatePersistenceService,
    protected siteContextParamsService: SiteContextParamsService,
    protected eventService: EventService
  ) {}

  getQuantity(): Observable<number> {
    return this.activeCartRequired().pipe(
      switchMap((activeCartRequired) => {
        if (activeCartRequired) {
          return this.activeCartFacade.getActive().pipe(
            startWith({ totalUnitCount: 0 }),
            map((cart: Cart) => {
              let amount = 0;
              cart.entries?.forEach(entry => {
                amount += entry.quantity ? entry.quantity : 0;
              });
              return amount;
            })
          );
        } else {
          return of(0);
        }
      })
    );
  }

  getTotalPrice(): Observable<string> {
    return this.activeCartRequired().pipe(
      switchMap((activeCartRequired) => {
        const zeroPrice = '€ 0,00';
        if (activeCartRequired) {
          return this.activeCartFacade
            .getActive()
            .pipe(map((cart) => {
              return cart.totalPrice?.formattedValue ?? zeroPrice;
            }));
        } else {
          return of(zeroPrice);
        }
      })
    );
  }

  protected activeCartRequired(): Observable<boolean> {
    return combineLatest([
      this.hasActiveCartInStorage(),
      this.authService.isUserLoggedIn(),
      this.isCartCreated(),
    ]).pipe(
      map(
        ([hasCartInStorage, isUserLoggedIn, isCartCreated]) =>
          hasCartInStorage || isUserLoggedIn || isCartCreated
      ),
      distinctUntilChanged(),
      takeWhile((hasCart) => !hasCart, true)
    );
  }

  protected hasActiveCartInStorage(): Observable<boolean> {
    return this.getCartStateFromBrowserStorage().pipe(
      map((state) => Boolean(state?.active))
    );
  }

  protected isCartCreated(): Observable<boolean> {
    return this.eventService.get(CreateCartEvent).pipe(
      map((_) => true),
      startWith(false)
    );
  }

  protected getCartStateFromBrowserStorage(): Observable<
    { active: string } | undefined
  > {
    return this.siteContextParamsService.getValues([BASE_SITE_CONTEXT_ID]).pipe(
      map((context) => {
        return this.statePersistenceService.readStateFromStorage({
          key: 'cart',
          context: context,
          storageType: StorageSyncType.LOCAL_STORAGE,
        });
      })
    );
  }
}
